import axios from 'axios';
import config from '@/services/config';

/**
 * API Wrapper - Centralizes HTTP request logic
 * Automatically handles: authentication, errors, response transformation
 */

/**
 * Gets the authentication token
 * @returns {string|null}
 */
const getAuthToken = () => localStorage.getItem('accessToken');

/**
 * Builds default headers with authentication
 * @param {Object} customHeaders - Additional headers
 * @returns {Object}
 */
const buildHeaders = (customHeaders = {}, noAuth = false) => {
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };
  if (!noAuth) {
    headers.Authorization = `Bearer ${getAuthToken()}`;
  }
  return headers;
};

/**
 * Builds the complete URL
 * @param {string} endpoint - Relative endpoint (e.g. '/api/v3/ai/chat/')
 * @returns {string}
 */
const buildUrl = (endpoint) => {
  const baseUrl = config.urlCore || '';
  // Remove duplicate slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`.replace(/([^:]\/)\/+/g, '$1');
};

/**
 * Handles errors in a centralized way
 * @param {Error} error - Axios error
 * @returns {Object} - Normalized error object
 */
const handleError = (error) => {
  const normalized = {
    success: false,
    message: 'An error occurred',
    status: null,
    data: null,
    originalError: error,
  };

  if (error.response) {
    normalized.status = error.response.status;
    normalized.message = error.response.data?.response?.message
      || error.response.data?.message
      || error.response.data?.detail
      || error.message;
    normalized.code = error.response.data?.response?.code || error.response.data?.code || null;
    normalized.data = error.response.data;
  } else if (error.request) {
    normalized.message = 'Network error: No response from server';
  } else {
    normalized.message = error.message;
  }

  console.error('[API Wrapper Error]:', normalized);
  return Promise.reject(normalized);
};

/**
 * Performs an HTTP request
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, PATCH)
 * @param {string} endpoint - Relative endpoint
 * @param {Object} options - Request options
 * @param {Object} options.data - Request body (POST, PUT, PATCH)
 * @param {Object} options.params - Query params (GET)
 * @param {Object} options.headers - Additional headers
 * @param {Object} options.config - Additional Axios configuration
 * @param {boolean} options.returnFullResponse - If true, returns full Axios response
 * @returns {Promise}
 */
const request = async (method, endpoint, options = {}) => {
  const {
    data = null,
    params = null,
    headers = {},
    config: customConfig = {},
    returnFullResponse = false,
    noAuth = false,
  } = options;

  const url = buildUrl(endpoint);
  const requestHeaders = buildHeaders(headers, noAuth);

  const axiosConfig = {
    method,
    url,
    headers: requestHeaders,
    ...customConfig,
  };

  if (data) axiosConfig.data = data;
  if (params) axiosConfig.params = params;

  try {
    const response = await axios(axiosConfig);
    
    // Return full response or just data
    return returnFullResponse ? response : response.data;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Convenience methods for each HTTP verb
 */
export const api = {
  /**
   * GET request
   * @param {string} endpoint
   * @param {Object} options
   * @returns {Promise}
   */
  get: (endpoint, options = {}) => request('GET', endpoint, options),

  /**
   * POST request
   * @param {string} endpoint
   * @param {Object} data - Request body
   * @param {Object} options
   * @returns {Promise}
   */
  post: (endpoint, data = {}, options = {}) => request('POST', endpoint, { ...options, data }),

  /**
   * PUT request
   * @param {string} endpoint
   * @param {Object} data - Request body
   * @param {Object} options
   * @returns {Promise}
   */
  put: (endpoint, data = {}, options = {}) => request('PUT', endpoint, { ...options, data }),

  /**
   * PATCH request
   * @param {string} endpoint
   * @param {Object} data - Request body
   * @param {Object} options
   * @returns {Promise}
   */
  patch: (endpoint, data = {}, options = {}) => request('PATCH', endpoint, { ...options, data }),

  /**
   * DELETE request
   * @param {string} endpoint
   * @param {Object} options
   * @returns {Promise}
   */
  delete: (endpoint, options = {}) => request('DELETE', endpoint, options),
};

/**
 * Helper for FormData requests (file uploads)
 * @param {string} endpoint
 * @param {FormData} formData
 * @param {Object} options
 * @returns {Promise}
 */
export const uploadFile = (endpoint, formData, options = {}) => {
  return api.post(endpoint, formData, {
    ...options,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...options.headers,
    },
  });
};

/**
 * Helper for downloading files (blobs)
 * @param {string} endpoint
 * @param {Object} options
 * @returns {Promise<Blob>}
 */
export const downloadFile = async (endpoint, options = {}) => {
  return request('GET', endpoint, {
    ...options,
    config: {
      responseType: 'blob',
      ...options.config,
    },
  });
};

export default api;
